import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, ArrowLeft, Check } from "lucide-react";
import api, { formatBRL } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setColor(r.data.colors?.[0] || null);
    }).catch(() => setProduct(false));
  }, [id]);

  if (product === null) return <div className="py-32 text-center font-serif text-2xl text-[#1A1A1A]/50">Carregando…</div>;
  if (product === false) return <div className="py-32 text-center font-serif text-2xl">Produto não encontrado.</div>;

  const handleAdd = () => {
    if (product.sizes?.length > 0 && !size) {
      toast.error("Selecione um tamanho");
      return;
    }
    addItem(product, { size, color, quantity: qty });
    toast.success(`${product.name} adicionado à sacola`);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
      <Link to="/loja" className="inline-flex items-center gap-2 overline text-[#1A1A1A]/60 hover:text-[#B89C65] transition-colors mb-8">
        <ArrowLeft size={16} /> Voltar à loja
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="flex flex-col-reverse md:flex-row gap-4">
          {product.images?.length > 1 && (
            <div className="flex md:flex-col gap-3">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                  className={`w-20 h-24 overflow-hidden border ${activeImg === i ? "border-[#B89C65]" : "border-transparent"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="flex-1 bg-[#EFEEEA] aspect-[3/4] overflow-hidden">
            <img src={product.images?.[activeImg]} alt={product.name} className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="lg:py-6">
          <div className="overline text-[#1A1A1A]/40 mb-3">{product.category}</div>
          <h1 data-testid="product-name" className="font-serif text-4xl md:text-5xl text-[#1A1A1A] leading-tight">{product.name}</h1>
          <p data-testid="product-price" className="text-2xl text-[#1A1A1A] mt-4">{formatBRL(product.price)}</p>
          <p className="text-[#1A1A1A]/70 leading-relaxed mt-6">{product.description}</p>

          {product.colors?.length > 0 && (
            <div className="mt-8">
              <div className="overline text-[#1A1A1A]/60 mb-3">Cor: {color}</div>
              <div className="flex gap-2">
                {product.colors.map((c) => (
                  <button key={c} onClick={() => setColor(c)} data-testid={`color-${c}`}
                    className={`px-4 py-2 border text-sm transition-colors ${color === c ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#F8F7F4]" : "border-[#1A1A1A]/20 text-[#1A1A1A]/70 hover:border-[#1A1A1A]"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.sizes?.length > 0 && (
            <div className="mt-6">
              <div className="overline text-[#1A1A1A]/60 mb-3">Tamanho</div>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                    className={`min-w-[52px] px-3 py-3 border text-sm transition-colors ${size === s ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#F8F7F4]" : "border-[#1A1A1A]/20 text-[#1A1A1A]/70 hover:border-[#1A1A1A]"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-[#1A1A1A]/20">
              <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:text-[#B89C65] transition-colors"><Minus size={16} /></button>
              <span data-testid="qty-value" className="w-10 text-center">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty(qty + 1)} className="p-3 hover:text-[#B89C65] transition-colors"><Plus size={16} /></button>
            </div>
            <span className="text-sm text-[#1A1A1A]/50">{product.stock > 0 ? `${product.stock} em estoque` : "Esgotado"}</span>
          </div>

          <button data-testid="add-to-cart-btn" onClick={handleAdd} disabled={product.stock <= 0}
            className="mt-8 w-full flex items-center justify-center gap-3 bg-[#1A1A1A] text-[#F8F7F4] py-5 hover:bg-[#B89C65] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            <Check size={18} /> <span className="overline">Adicionar à sacola</span>
          </button>
        </div>
      </div>
    </div>
  );
}
